import React, { useState, useEffect } from 'react';
import CurrencyInput from "react-currency-input-field";
import Select from "react-select";
import api from '../../../../config/axios';
import moment from 'moment';
import Swal from 'sweetalert2';
import DateTimePicker, { dateTimeHelpers } from '../../../../components/utils/datetimepicker';

import { autoCompleteClientes, autoCompleteServicos, autoCompleteProdutos, autoCompleteColaboradoresComanda } from "../../../../components/utils/autocompletes";
import { FaRegSave, FaTimes, FaEdit, FaTrashAlt, FaPlus, FaThumbsUp, FaUserPlus, FaUserMinus } from 'react-icons/fa';

const ComandaAbertaModal = ({ comanda, onClose, onComandaAtualizada }) => {
    const [comandaAtual, setComandaAtual] = useState(comanda);
    const [tituloModal, setTituloModal] = useState("");
    const [activeTab, setActiveTab] = useState('itens');
    const [caixas, setCaixas] = useState([]);
    const [maquinasList, setMaquinasList] = useState([]);
    const [windowWidth, setWindowWidth] = useState(window.innerWidth);
    const [contasBancarias, setContasBancarias] = useState([]);
    const [bandeirasCartao, setBandeirasCartao] = useState([]);
    const [comandaSalva, setComandaSalva] = useState(false);
    const { inputPesquisaCliente, clientesOptions, handleAutoCompleteClientes, handleFocusClientes } = autoCompleteClientes();
    const { inputPesquisaColaborador, colaboradoresOptions, handleAutoCompleteColaboradores, handleFocusColaboradoresComanda } = autoCompleteColaboradoresComanda();
    const { inputPesquisaServico, servicosOptions, handleAutoCompleteServicos, handleFocusServicos } = autoCompleteServicos();
    const { inputPesquisaProduto, produtosOptions, handleAutoCompleteProdutos, handleFocusProdutos } = autoCompleteProdutos();

    const [formData, setFormData] = useState({
        id: '',
        cliente: '',
        data_abertura: new Date(),
        status: 'aberta',
        observacao: '',
        caixa_responsavel: '',
        maquina_caixa: '',
        itens: [],
        pagamentos: []
    });

    const customStyles = {
        menu: (provided) => ({
            ...provided,
            whiteSpace: "pre-line",
        }),
        option: (provided) => ({
            ...provided,
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
        }),
    };

    useEffect(() => {
        const carregarDados = async () => {
            if (comanda != null) {
                setComandaAtual(comanda);
                setFormData({
                    ...comanda,
                    data_abertura: dateTimeHelpers.stringToDateTime(comanda.data_abertura) || new Date()
                });
                setTituloModal('Editar Comanda');
                setComandaSalva(true);

                if (comanda.pagamentos && comanda.pagamentos.length > 0) {
                    for (let i = 0; i < comanda.pagamentos.length; i++) {
                        const pagamento = comanda.pagamentos[i];
                        if ((pagamento.tipo_pagamento === 'pagamento_credito' || pagamento.tipo_pagamento === 'pagamento_debito') && pagamento.conta_bancaria_id) {
                            const bandeiras = await fetchBandeirasCartao(pagamento.conta_bancaria_id, pagamento.tipo_pagamento);
                            setBandeirasCartao(prevState => ({
                                ...prevState,
                                [pagamento.id]: bandeiras
                            }));
                        }
                    }
                }
            }
            else {
                setComandaAtual(null);
                setTituloModal('Nova Comanda');
                setComandaSalva(false);
            }
            obterCaixas();
            fetchMaquinas();
            fetchContasBancarias();
        };

        carregarDados();
    }, [comanda]);

    useEffect(() => {
        const handleResize = () => {
            setWindowWidth(window.innerWidth);
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const obterCaixas = async () => {
        const response = await api.get(`/caixas?status=aberto`);
        setCaixas(response.data);

        if (formData.caixa_responsavel) {
            const caixaSelecionado = response.data.find(caixa => caixa.id.toString() === formData.caixa_responsavel.toString());
            if (caixaSelecionado && caixaSelecionado.maquina_id) {
                const maquinaSelecionada = maquinasList.find(maq => maq.id.toString() === caixaSelecionado.maquina_id.toString());
                if (maquinaSelecionada) {
                    setFormData(prevState => ({
                        ...prevState,
                        maquina_caixa: {
                            id: maquinaSelecionada.id,
                            serial: maquinaSelecionada.serial
                        }
                    }));
                }
            }
        }
    }

    const fetchMaquinas = async () => {
        try {
            Swal.fire({
                title: "Carregando...",
                allowOutsideClick: false,
                allowEscapeKey: false,
                showConfirmButton: false,
                didOpen: () => {
                    Swal.showLoading();
                }
            });

            const response = await api.get('/maquinas-pagamento?page=1&limit=200&status=ativo');
            setMaquinasList(response.data.records || []);
            Swal.close();
        } catch (error) {
            Swal.close();
        }
    };

    const fetchContasBancarias = async () => {
        try {
            const response = await api.get('/contas-bancarias/para-lancamentos');
            setContasBancarias(response.data || []);
        } catch (error) {
        }
    };

    const fetchBandeirasCartao = async (contaBancariaId, tipo) => {
        try {
            if (tipo === 'pagamento_credito') {
                const responseAvista = await api.get(`/cartoes?conta_bancaria_id=${contaBancariaId}&tipo=credito_avista&limit=1000`);
                const responseParcelado = await api.get(`/cartoes?conta_bancaria_id=${contaBancariaId}&tipo=credito_parcelado&limit=1000`);

                const bandeirasAvista = (responseAvista.data.records || []).map(b => ({ ...b, tipo_exibicao: 'À Vista' }));
                const bandeirasParcelado = (responseParcelado.data.records || []).map(b => ({ ...b, tipo_exibicao: 'Parcelado' }));

                return [...bandeirasAvista, ...bandeirasParcelado];
            } else {
                const response = await api.get(`/cartoes?conta_bancaria_id=${contaBancariaId}&tipo=debito&limit=1000`);
                return response.data.records || [];
            }
        } catch (error) {
            return [];
        }
    };

    const calcularTotal = (itens) => {
        let total = 0;
        for (let i = 0; i < itens.length; i++) {
            total += parseFloat(itens[i].final_item);
        }
        return total.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
    };

    const handleChange = (e) => {
        const { name, value } = e.target;

        setFormData({
            ...formData,
            [name]: value
        });

        if (name === 'maquina_id') {
            const maquinaSelecionada = maquinasList.find(maq => maq.id.toString() === value);
            if (maquinaSelecionada) {
                setFormData(prevState => ({
                    ...prevState,
                    numero_serial: maquinaSelecionada.serial || ''
                }));
            }
        }
    };

    const handleSelectChange = async (name, value) => {
        if (name === 'caixa_responsavel') {
            const caixaSelecionado = caixas.find(caixa => caixa.id.toString() === value);
            const contaBancariaCaixa = caixaSelecionado?.conta_bancaria_id || '';

            if (caixaSelecionado && caixaSelecionado.maquina_id) {
                const maquinaSelecionada = maquinasList.find(maq => maq.id.toString() === caixaSelecionado.maquina_id.toString());

                setFormData(prevState => ({
                    ...prevState,
                    [name]: value,
                    maquina_caixa: maquinaSelecionada ? {
                        id: maquinaSelecionada.id,
                        serial: maquinaSelecionada.serial
                    } : null
                }));

                setFormData(prevState => ({
                    ...prevState,
                    pagamentos: prevState.pagamentos.map(pagamento => {
                        if (pagamento.tipo_pagamento === 'pagamento_credito' || pagamento.tipo_pagamento === 'pagamento_debito') {
                            return {
                                ...pagamento,
                                maquina_id: maquinaSelecionada ? maquinaSelecionada.id : '',
                                numero_serial: maquinaSelecionada ? maquinaSelecionada.serial : '',
                                conta_bancaria_id: contaBancariaCaixa
                            };
                        } else if (pagamento.tipo_pagamento === 'pagamento_pix') {
                            return {
                                ...pagamento,
                                conta_bancaria_id: contaBancariaCaixa
                            };
                        }
                        return pagamento;
                    })
                }));

                if (contaBancariaCaixa) {
                    for (let i = 0; i < formData.pagamentos.length; i++) {
                        const pagamento = formData.pagamentos[i];
                        if (pagamento.tipo_pagamento === 'pagamento_credito' || pagamento.tipo_pagamento === 'pagamento_debito') {
                            const bandeiras = await fetchBandeirasCartao(contaBancariaCaixa, pagamento.tipo_pagamento);
                            setBandeirasCartao(prevState => ({
                                ...prevState,
                                [pagamento.id]: bandeiras
                            }));
                        }
                    }
                }

                return;
            }
        }

        setFormData((prevState) => ({
            ...prevState,
            [name]: value || "",
        }));
    };

    const handleChangeItem = (id, field, value) => {
        if (field == 'servico') {
            if (value == null) {
                setFormData((prevState) => ({
                    ...prevState,
                    itens: prevState.itens.map((item) =>
                        item.id === id ? { ...item, servico: '', profissional: '', assistentes: [], produto: '', valor_item: '', desconto_item: '', final_item: '' } : item
                    ),
                }));
                return;
            }

            setFormData((prevFormData) => ({
                ...prevFormData,
                itens: prevFormData.itens.map((item) =>
                    item.id === id ? { ...item, [field]: value, profissional: '', assistentes: [], valor_item: value.preco, final_item: value.preco } : item
                ),
            }));
            return;
        }

        if (field == 'produto') {
            if (value == null) {
                setFormData((prevState) => ({
                    ...prevState,
                    itens: prevState.itens.map((item) =>
                        item.id === id ? { ...item, produto: '', servico: '', profissional: '', assistentes: [], valor_item: '', desconto_item: '', final_item: '' } : item
                    ),
                }));
                return;
            }

            setFormData((prevFormData) => ({
                ...prevFormData,
                itens: prevFormData.itens.map((item) =>
                    item.id === id ? { ...item, [field]: value, profissional: '', assistentes: [], valor_item: value.preco, final_item: value.preco } : item
                ),
            }));
            return;
        }

        if (field == 'profissional') {
            setFormData((prevFormData) => ({
                ...prevFormData,
                itens: prevFormData.itens.map((item) =>
                    item.id === id ? { ...item, [field]: value, assistentes: [] } : item
                ),
            }));
            return;
        }

        if (field == 'qtd') {
            setFormData((prevFormData) => ({
                ...prevFormData,
                itens: prevFormData.itens.map((currentItem) => {
                    if (currentItem.id === id) {
                        const valor = (currentItem.tipo_item == 'servico') ? currentItem.servico.preco * parseInt(value) : currentItem.produto.preco * parseInt(value);
                        return {
                            ...currentItem,
                            [field]: value,
                            valor_item: valor.toFixed(2),
                            final_item: valor - (valor * currentItem.desconto_item.replace(/[^0-9.]/g, "")) / 100
                        };
                    }
                    return currentItem;
                }),
            }));
            return;
        }

        if (field == 'valor_item') {
            setFormData((prevFormData) => ({
                ...prevFormData,
                itens: prevFormData.itens.map((currentItem) => {
                    if (!value) value = '0,00';
                    if (currentItem.id === id) {
                        let finalItem;
                        if (currentItem.tipo_item === 'caixinha') {
                            finalItem = parseFloat(value.replace(',', '.'));
                        } else {
                            finalItem = value.replace(',', '.') - (value.replace(',', '.') * currentItem.desconto_item.replace(/[^0-9.]/g, "")) / 100;
                        }

                        return {
                            ...currentItem,
                            [field]: value,
                            final_item: finalItem
                        };
                    }
                    return currentItem;
                }),
            }));
            return;
        }

        if (field == 'desconto_item') {
            setFormData((prevFormData) => ({
                ...prevFormData,
                itens: prevFormData.itens.map((currentItem) => {
                    if (currentItem.id === id) {
                        currentItem.valor_item = currentItem.valor_item.replace(',', '.');
                        value = value.replace(/[^0-9.]/g, "");
                        let inputValue = value
                        if (parseFloat(inputValue) > 100) {
                            inputValue = "100";
                            value = 100;
                        }
                        if (inputValue != '') inputValue = inputValue + "%"
                        return {
                            ...currentItem,
                            [field]: inputValue,
                            final_item: currentItem.valor_item - (currentItem.valor_item * value) / 100
                        };
                    }
                    return currentItem;
                }),
            }));
            return;
        }

        setFormData((prevFormData) => ({
            ...prevFormData,
            itens: prevFormData.itens.map((item) =>
                item.id === id ? { ...item, [field]: value } : item
            ),
        }));
    };

    const handleAdicionarItem = (tipo) => {
        const novoItem = {
            id: `temp_${new Date().getTime()}`,
            id_item: '',
            tipo_item: tipo,
            servico: '',
            produto: '',
            profissional: '',
            assistentes: [],
            qtd: 1,
            valor_item: 0,
            desconto_item: '0%',
            final_item: 0,
            editar: true
        };

        setFormData((prevState) => ({
            ...prevState,
            itens: [...prevState.itens, novoItem],
        }));
    };

    const handleAdicionarAssistente = (itemId) => {
        setFormData((prevState) => ({
            ...prevState,
            itens: prevState.itens.map((item) =>
                item.id === itemId
                    ? {
                        ...item,
                        assistentes: [...(item.assistentes || []), {
                            id: `temp_${new Date().getTime()}`,
                            profissional: null
                        }]
                    }
                    : item
            ),
        }));
    };

    const handleRemoverAssistente = (itemId, assistenteId) => {
        setFormData((prevState) => ({
            ...prevState,
            itens: prevState.itens.map((item) =>
                item.id === itemId
                    ? {
                        ...item,
                        assistentes: (item.assistentes || []).filter((a) => a.id !== assistenteId)
                    }
                    : item
            ),
        }));
    };

    const handleChangeAssistente = (itemId, assistenteId, field, value) => {
        setFormData((prevState) => ({
            ...prevState,
            itens: prevState.itens.map((item) =>
                item.id === itemId
                    ? {
                        ...item,
                        assistentes: (item.assistentes || []).map((assistente) =>
                            assistente.id === assistenteId
                                ? { ...assistente, [field]: value }
                                : assistente
                        )
                    }
                    : item
            ),
        }));
    };

    const handleAdicionarPagamento = async () => {
        const caixaSelecionado = caixas.find(caixa => caixa.id.toString() === formData.caixa_responsavel.toString());
        const contaBancariaCaixa = caixaSelecionado?.conta_bancaria_id || '';

        const novoItem = {
            id: new Date().getTime(),
            tipo_pagamento: 'pagamento_dinheiro',
            valor: 0,
            informacoes: '',
            maquina_id: '',
            numero_serial: '',
            status_pagamento: 'pendente',
            conta_bancaria_id: '',
            bandeira_id: ''
        };

        setFormData((prevState) => {
            const novoPagamento = { ...novoItem };

            if ((novoPagamento.tipo_pagamento === 'pagamento_credito' ||
                novoPagamento.tipo_pagamento === 'pagamento_debito') &&
                prevState.maquina_caixa) {
                novoPagamento.maquina_id = prevState.maquina_caixa.id;
                novoPagamento.numero_serial = prevState.maquina_caixa.serial;
            }

            return {
                ...prevState,
                pagamentos: [...prevState.pagamentos, novoPagamento],
            };
        });
    };

    const handleChangePagamento = async (id, field, value) => {
        if (field === 'tipo_pagamento') {
            const caixaSelecionado = caixas.find(caixa => caixa.id.toString() === formData.caixa_responsavel.toString());
            const contaBancariaCaixa = caixaSelecionado?.conta_bancaria_id || '';

            if (value === 'pagamento_credito' || value === 'pagamento_debito') {
                const pagamentoAtual = formData.pagamentos.find(p => p.id === id);
                const contaBancariaAtual = pagamentoAtual?.conta_bancaria_id || contaBancariaCaixa;

                if (contaBancariaAtual) {
                    const bandeiras = await fetchBandeirasCartao(contaBancariaAtual, value);
                    setBandeirasCartao(prevState => ({
                        ...prevState,
                        [id]: bandeiras
                    }));
                }

                setFormData((prevFormData) => {
                    const pagamentoAtualizado = { ...prevFormData.pagamentos.find(p => p.id === id) };
                    pagamentoAtualizado[field] = value;
                    pagamentoAtualizado.conta_bancaria_id = contaBancariaAtual;
                    pagamentoAtualizado.bandeira_id = '';

                    if (prevFormData.maquina_caixa) {
                        pagamentoAtualizado.maquina_id = prevFormData.maquina_caixa.id;
                        pagamentoAtualizado.numero_serial = prevFormData.maquina_caixa.serial;
                    }

                    return {
                        ...prevFormData,
                        pagamentos: prevFormData.pagamentos.map(item =>
                            item.id === id ? pagamentoAtualizado : item
                        )
                    };
                });
                return;
            } else if (value === 'pagamento_pix') {
                setFormData(prevState => ({
                    ...prevState,
                    pagamentos: prevState.pagamentos.map(item => {
                        if (item.id === id) {
                            return {
                                ...item,
                                [field]: value,
                                maquina_id: '',
                                numero_serial: '',
                                conta_bancaria_id: contaBancariaCaixa,
                                bandeira_id: ''
                            };
                        }
                        return item;
                    }),
                }));
                return;
            } else {
                setFormData(prevState => ({
                    ...prevState,
                    pagamentos: prevState.pagamentos.map(item => {
                        if (item.id === id) {
                            return {
                                ...item,
                                [field]: value,
                                maquina_id: '',
                                numero_serial: '',
                                conta_bancaria_id: '',
                                bandeira_id: ''
                            };
                        }
                        return item;
                    }),
                }));
                return;
            }
        }

        if (field === 'conta_bancaria_id') {
            const pagamento = formData.pagamentos.find(p => p.id === id);
            if (pagamento && (pagamento.tipo_pagamento === 'pagamento_credito' || pagamento.tipo_pagamento === 'pagamento_debito')) {
                const bandeiras = await fetchBandeirasCartao(value, pagamento.tipo_pagamento);
                setBandeirasCartao(prevState => ({
                    ...prevState,
                    [id]: bandeiras
                }));
            }

            setFormData((prevFormData) => ({
                ...prevFormData,
                pagamentos: prevFormData.pagamentos.map((item) => {
                    if (item.id === id) {
                        return { ...item, [field]: value, bandeira_id: '' };
                    }
                    return item;
                }),
            }));
            return;
        }

        setFormData((prevFormData) => ({
            ...prevFormData,
            pagamentos: prevFormData.pagamentos.map((item) => {
                if (item.id === id) {
                    if (field === 'numero_serial') {
                        const maquinaSelecionada = maquinasList.find(maq => maq.serial === value);
                        return {
                            ...item,
                            [field]: value,
                            maquina_id: maquinaSelecionada ? maquinaSelecionada.id || '' : ''
                        };
                    }

                    return { ...item, [field]: value };
                }
                return item;
            }),
        }));
    };

    const handleDeleteItem = (item) => {
        setFormData((prevState) => ({
            ...prevState,
            itens: prevState.itens.filter((i) => i.id !== item.id),
        }));
    };

    const handleEditItem = (item) => {
        setFormData((prevState) => ({
            ...prevState,
            itens: prevState.itens.map((i) =>
                i.id === item.id ? { ...i, editar: true } : i
            ),
        }));
    };

    const handleDeletePagamento = (pagamento) => {
        setFormData((prevState) => ({
            ...prevState,
            pagamentos: prevState.pagamentos.filter((p) => p.id !== pagamento.id),
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const camposObrigatorios = [
            { campo: 'cliente', label: 'Cliente' },
            { campo: 'data_abertura', label: 'Data' }
        ];

        const camposInvalidos = camposObrigatorios.filter(({ campo }) => {
            const valor = formData[campo];
            if (campo === 'cliente') {
                return !valor || !valor.value;
            }
            if (typeof valor === 'string') {
                return !valor.trim();
            }

            return !valor;
        });

        if (camposInvalidos.length > 0) {
            Swal.fire(
                'Erro',
                `Os seguintes campos são obrigatórios: ${camposInvalidos.map(c => c.label).join(', ')}`,
                'error'
            );
            return;
        }

        if (formData.itens.length === 0) {
            Swal.fire('Erro', 'Adicione pelo menos um item à comanda', 'error');
            return;
        }

        for (let i = 0; i < formData.itens.length; i++) {
            const item = formData.itens[i];

            if (item.tipo_item === 'caixinha') {
                if (!item.profissional || !item.profissional.value) {
                    Swal.fire('Erro', `O item ${i + 1} (Caixinha) precisa ter um profissional selecionado`, 'error');
                    return;
                }
            } else {
                if (item.servico === '' && item.produto === '') {
                    Swal.fire('Erro', `O item ${i + 1} não possui serviço ou produto selecionado`, 'error');
                    return;
                }

                if (item.tipo_item === 'servico' && (!item.profissional || !item.profissional.value)) {
                    Swal.fire('Erro', `O item ${i + 1} é um serviço e precisa ter um profissional selecionado`, 'error');
                    return;
                }
            }
        }

        const data = {
            id_pessoa_cliente: formData.cliente.value,
            status: 'aberta',
            observacao: formData.observacao,
            id_caixa: formData.caixa_responsavel,
            itens: [],
            pagamentos: []
        };

        for (let i = 0; i < formData.itens.length; i++) {
            const item = formData.itens[i];
            const id = (item.tipo_item == 'servico') ? item.servico.value : item.produto.value;
            const desconto = (item.desconto_item) ? parseFloat(item.desconto_item.replace(/[^0-9.]/g, "")) : 0
            const valor = (item.valor_item) ? parseFloat(item.valor_item.replace(',', '.').replace(/[^0-9.]/g, "")) : 0

            const colaboradorId = item.profissional && item.profissional.value
                ? item.profissional.value
                : null;

            const itemData = {
                id_item: id,
                tipo_item: item.tipo_item,
                id_pessoa_colaborador: colaboradorId,
                qtd: item.qtd,
                valor_item: valor,
                desconto_item: desconto
            };

            if (item.id && !item.id.toString().startsWith('temp')) {
                itemData.id = item.id;
            }

            if (item.tipo_item === 'servico' && item.assistentes && item.assistentes.length > 0) {
                itemData.assistentes = item.assistentes
                    .filter(assistente => assistente.profissional && assistente.profissional.value)
                    .map(assistente => ({
                        id_pessoa_colaborador: assistente.profissional.value
                    }));
            }

            data.itens.push(itemData);
        }

        for (let i = 0; i < formData.pagamentos.length; i++) {
            data.pagamentos.push({
                id: formData.pagamentos[i].id,
                tipo_pagamento: formData.pagamentos[i].tipo_pagamento,
                valor: (formData.pagamentos[i].valor) ? parseFloat(formData.pagamentos[i].valor.replace(',', '.')) : 0,
                informacoes: formData.pagamentos[i].informacoes,
                maquina_id: formData.pagamentos[i].maquina_id,
                numero_serial: formData.pagamentos[i].numero_serial,
                status_pagamento: formData.pagamentos[i].status_pagamento || 'pendente',
                conta_bancaria_id: formData.pagamentos[i].conta_bancaria_id || null,
                bandeira_id: formData.pagamentos[i].bandeira_id || null
            })
        }

        try {
            let response;
            if (comandaAtual != null) {
                response = await api.put(`/comandas/${comandaAtual.id}`, data);
            }
            else {
                response = await api.post(`/comandas`, data);
            }

            if (response.data) {
                const comandaAtualizada = response.data;

                const comandaCompleta = await api.get(`/comandas/${comandaAtualizada.id}`);

                if (comandaCompleta.data) {
                    const comandaData = comandaCompleta.data;

                    const dadosAtualizados = {
                        id: comandaData.id,
                        cliente: {
                            label: comandaData.cliente.nome,
                            value: comandaData.id_pessoa_cliente
                        },
                        data_abertura: new Date(comandaData.data_abertura),
                        status: comandaData.status,
                        observacao: comandaData.observacao,
                        caixa_responsavel: comandaData.id_caixa,
                        itens: [],
                        pagamentos: comandaData.pagamentos || [],
                        criado_por: comandaData.criado_por,
                        criado_em: comandaData.criado_em,
                        alterado_por: comandaData.alterado_por,
                        alterado_em: comandaData.alterado_em
                    };

                    for (let i = 0; i < comandaData.itens.length; i++) {
                        const item = comandaData.itens[i];

                        const novoItem = {
                            id: item.id,
                            id_item: item.id_item,
                            tipo_item: item.tipo_item,
                            qtd: item.qtd,
                            profissional: item.colaborador ? {
                                label: item.colaborador.nome,
                                value: item.id_pessoa_colaborador
                            } : null,
                            assistentes: (item.assistentes || []).map(assistente => ({
                                id: assistente.id,
                                profissional: assistente.profissional
                            })),
                            valor_item: item.valor_item,
                            desconto_item: item.desconto_item + '%',
                            final_item: parseFloat(item.valor_item) - (parseFloat(item.valor_item) * (parseFloat(item.desconto_item) / 100)),
                            editar: false,
                        };

                        if (item.servico) {
                            novoItem.servico = {
                                label: item.servico.nome,
                                value: item.servico.id_servico,
                                preco: item.servico.preco
                            };
                        } else if (item.produto) {
                            novoItem.produto = {
                                label: item.produto.nome,
                                value: item.produto.id_produto,
                                preco: item.produto.valor_venda
                            };
                        }
                        dadosAtualizados.itens.push(novoItem);
                    }

                    for (let i = 0; i < dadosAtualizados.pagamentos.length; i++) {
                        const pagamento = dadosAtualizados.pagamentos[i];
                        if ((pagamento.tipo_pagamento === 'pagamento_credito' || pagamento.tipo_pagamento === 'pagamento_debito') && pagamento.conta_bancaria_id) {
                            const bandeiras = await fetchBandeirasCartao(pagamento.conta_bancaria_id, pagamento.tipo_pagamento);
                            setBandeirasCartao(prevState => ({
                                ...prevState,
                                [pagamento.id]: bandeiras
                            }));
                        }
                    }

                    setFormData(dadosAtualizados);

                    if (comandaAtual == null) {
                        setTituloModal('Editar Comanda');
                        setComandaSalva(true);
                    }

                    setComandaAtual(dadosAtualizados);
                }
            }

            onComandaAtualizada();
            Swal.fire('Sucesso', 'Comanda salva com sucesso!', 'success');
        }
        catch (error) {
            Swal.close();
            Swal.fire('Erro', `Erro ao salvar: ${error.message}`, 'error');
        }
    };

    const calcularTroco = () => {
        try {
            const totalPagamentos = formData.pagamentos.reduce((acc, pagamento) => {
                if (!pagamento.valor) return acc;

                const valorLimpo = pagamento.valor.toString()
                    .replace('R$', '')
                    .trim();

                const valorNumerico = valorLimpo.includes(',')
                    ? valorLimpo.replace(/\./g, '').replace(',', '.')
                    : valorLimpo;

                const valor = parseFloat(valorNumerico);
                return isNaN(valor) ? acc : acc + valor;
            }, 0);

            const totalComanda = formData.itens.reduce((acc, item) => {
                if (!item.final_item) return acc;
                return acc + parseFloat(item.final_item);
            }, 0);

            return totalPagamentos - totalComanda;
        } catch (error) {
            return 0;
        }
    };

    const formatarMoeda = (valor) => {
        return valor.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
    };

    const handleFinalizarComanda = async (e) => {
        e.preventDefault();

        const camposObrigatorios = [
            { campo: 'cliente', label: 'Cliente' },
            { campo: 'data_abertura', label: 'Data' },
            { campo: 'caixa_responsavel', label: 'Caixa Responsável' }
        ];

        const camposInvalidos = camposObrigatorios.filter(({ campo }) => {
            const valor = formData[campo];
            if (campo === 'cliente') {
                return !valor || !valor.value;
            }

            if (typeof valor === 'string') {
                return !valor.trim();
            }

            return !valor;
        });

        if (camposInvalidos.length > 0) {
            Swal.fire(
                'Erro',
                `Os seguintes campos são obrigatórios: ${camposInvalidos.map(c => c.label).join(', ')}`,
                'error'
            );
            return;
        }

        if (formData.itens.length === 0) {
            Swal.fire('Erro', 'Adicione pelo menos um item à comanda', 'error');
            return;
        }

        for (let i = 0; i < formData.itens.length; i++) {
            if (formData.itens[i].tipo_item === 'caixinha') {
                if (!formData.itens[i].profissional || !formData.itens[i].profissional.value) {
                    Swal.fire('Erro', `O item ${i + 1} (Caixinha) precisa ter um profissional selecionado`, 'error');
                    return;
                }
            }
            else {
                if (formData.itens[i].servico === '' && formData.itens[i].produto === '') {
                    Swal.fire('Erro', `O item ${i + 1} não possui serviço ou produto selecionado`, 'error');
                    return;
                }

                if (formData.itens[i].tipo_item === 'servico' && (!formData.itens[i].profissional || !formData.itens[i].profissional.value)) {
                    Swal.fire('Erro', `O item ${i + 1} é um serviço e precisa ter um profissional selecionado`, 'error');
                    return;
                }
            }
        }

        const data = {
            id_pessoa_cliente: formData.cliente.value,
            status: 'aberta',
            observacao: formData.observacao,
            id_caixa: formData.caixa_responsavel,
            itens: [],
            pagamentos: []
        };

        for (let i = 0; i < formData.itens.length; i++) {
            const item = formData.itens[i];
            let id = '';
            if (item.tipo_item == 'caixinha') {
                id = null;
            }
            else {
                id = (item.tipo_item == 'servico') ? item.servico.value : item.produto.value;
            }

            const desconto = (item.desconto_item) ? parseFloat(item.desconto_item.replace(/[^0-9.]/g, "")) : 0
            const valor = (item.valor_item) ? parseFloat(item.valor_item.replace(',', '.').replace(/[^0-9.]/g, "")) : 0

            const colaboradorId = item.profissional && item.profissional.value
                ? item.profissional.value
                : null;

            const itemData = {
                id_item: id,
                tipo_item: item.tipo_item,
                id_pessoa_colaborador: colaboradorId,
                qtd: item.qtd,
                valor_item: valor,
                desconto_item: desconto
            };

            if (item.id && !item.id.toString().startsWith('temp')) {
                itemData.id = item.id;
            }

            if (item.tipo_item === 'servico' && item.assistentes && item.assistentes.length > 0) {
                itemData.assistentes = item.assistentes
                    .filter(assistente => assistente.profissional && assistente.profissional.value)
                    .map(assistente => ({
                        id_pessoa_colaborador: assistente.profissional.value
                    }));
            }

            data.itens.push(itemData);
        }

        for (let i = 0; i < formData.pagamentos.length; i++) {
            data.pagamentos.push({
                id: formData.pagamentos[i].id,
                tipo_pagamento: formData.pagamentos[i].tipo_pagamento,
                valor: (formData.pagamentos[i].valor) ? parseFloat(formData.pagamentos[i].valor.replace(',', '.')) : 0,
                informacoes: formData.pagamentos[i].informacoes,
                maquina_id: formData.pagamentos[i].maquina_id,
                numero_serial: formData.pagamentos[i].numero_serial,
                status_pagamento: formData.pagamentos[i].status_pagamento || 'pendente',
                conta_bancaria_id: formData.pagamentos[i].conta_bancaria_id || null,
                bandeira_id: formData.pagamentos[i].bandeira_id || null
            })
        }

        try {
            Swal.fire({
                title: "Processando!",
                allowOutsideClick: false,
                allowEscapeKey: false,
                showConfirmButton: false,
                didOpen: () => {
                    Swal.showLoading();
                }
            });

            if (comandaSalva) {
                const comandaAtualizada = await api.put(`/comandas/${formData.id}/validar-encerramento`, data);
                setComandaAtual(comandaAtualizada.data);
                formData.pagamentos = comandaAtualizada.data.pagamentos;
                Swal.close();

                const processarPagamentos = async () => {
                    let todosProcessadosComSucesso = true;

                    if (formData.pagamentos.length === 0) {
                        await Swal.fire('Erro', 'É necessário adicionar pelo menos uma forma de pagamento para finalizar a comanda.', 'error');
                        return false;
                    }

                    const totalPagamentos = formData.pagamentos.reduce((acc, pagamento) => {
                        if (!pagamento.valor) return acc;
                        const valorLimpo = pagamento.valor.toString().replace('R$', '').trim();
                        const valorNumerico = valorLimpo.includes(',')
                            ? valorLimpo.replace(/\./g, '').replace(',', '.')
                            : valorLimpo;
                        return acc + parseFloat(valorNumerico || 0);
                    }, 0);

                    const totalComanda = formData.itens.reduce((acc, item) => {
                        if (!item.final_item) return acc;
                        return acc + parseFloat(item.final_item);
                    }, 0);

                    const tolerancia = 0.01;
                    if (totalPagamentos < (totalComanda - tolerancia)) {
                        await Swal.fire('Erro', 'O valor total dos pagamentos deve ser igual ou maior que o valor total da comanda.', 'error');
                        return false;
                    }

                    for (let i = 0; i < formData.pagamentos.length; i++) {
                        const pagamento = formData.pagamentos[i];
                        if (pagamento.status_pagamento == 'aprovado') {
                            continue;
                        }
                        pagamento.status_pagamento = 'pendente';
                        try {
                            if (pagamento.tipo_pagamento === 'pagamento_credito' || pagamento.tipo_pagamento === 'pagamento_debito') {
                                if (pagamento.numero_serial) {
                                    /* CÓDIGO ANTERIOR COMENTADO
                                    let pagamentoCancelado = false;
 
                                    Swal.fire({
                                        title: "Processando pagamento...",
                                        text: `Aguardando a conclusão do pagamento Nº ${i + 1} na máquina ${pagamento.numero_serial} no valor de ${formatarMoeda(parseFloat(pagamento.valor))}`,
                                        allowOutsideClick: false,
                                        allowEscapeKey: false,
                                        showConfirmButton: true,
                                        confirmButtonText: 'Cancelar Operação',
                                        showCancelButton: false,
                                        showLoaderOnConfirm: false,
                                        didOpen: () => {
                                            const content = Swal.getHtmlContainer();
                                            if (content) {
                                                const loader = document.createElement('div');
                                                loader.className = 'swal-loader';
                                                loader.innerHTML = '<div class="spinner-border text-primary" role="status"><span class="visually-hidden">Carregando...</span></div>';
                                                loader.style.marginTop = '20px';
                                                content.appendChild(loader);
                                            }
                                        }
                                    }).then((result) => {
                                        if (result.isConfirmed) {
                                            pagamentoCancelado = true;
                                            Swal.close();
                                            handleChangePagamento(pagamento.id, 'status_pagamento', 'pendente');
                                            todosProcessadosComSucesso = false;
                                            return "pendente";
                                        }
                                    });
 
                                    const verificarStatusCartao = () => {
                                        return new Promise((resolve, reject) => {
                                            const checkStatus = async () => {
                                                if (pagamentoCancelado) {
                                                    resolve("cancelado");
                                                    return;
                                                }
 
                                                try {
                                                    const statusResponse = await api.get(`/comanda-pagamentos/${pagamento.id}/status`);
 
                                                    if (statusResponse.data.sucesso) {
                                                        const status_pagamento = statusResponse.data.dados.status_pagamento;
                                                        if (status_pagamento === 'aprovado') {
                                                            Swal.close();
                                                            pagamento.status_pagamento = 'aprovado';
                                                            handleChangePagamento(pagamento.id, 'status_pagamento', 'aprovado');
                                                            await Swal.fire('Sucesso', 'Pagamento aprovado com sucesso!', 'success');
                                                            resolve("aprovado");
                                                            return;
                                                        } else if (status_pagamento === 'erro' || status_pagamento === 'recusado') {
                                                            Swal.close();
                                                            pagamento.status_pagamento = status_pagamento;
                                                            handleChangePagamento(pagamento.id, 'status_pagamento', 'erro');
                                                            todosProcessadosComSucesso = false;
                                                            await Swal.fire('Falha', 'Pagamento recusado', 'error');
                                                            resolve("recusado");
                                                            return;
                                                        }
                                                    }
 
                                                    if (!pagamentoCancelado) {
                                                        setTimeout(checkStatus, 5000);
                                                    } else {
                                                        resolve("cancelado");
                                                    }
                                                } catch (error) {
                                                    handleChangePagamento(pagamento.id, 'status_pagamento', 'erro');
                                                    todosProcessadosComSucesso = false;
                                                    await Swal.fire('Erro', 'Erro ao verificar status do pagamento', 'error');
                                                    reject(error);
                                                }
                                            };
 
                                            checkStatus();
                                        });
                                    };
 
                                    await verificarStatusCartao();
                                    */

                                    const confirmarPagamentoCartao = () => {
                                        return new Promise((resolve) => {
                                            const tipoCartao = pagamento.tipo_pagamento === 'pagamento_credito' ? 'Crédito' : 'Débito';
                                            Swal.fire({
                                                title: 'Confirmar Pagamento',
                                                text: `Por favor, confirme quando o pagamento de ${formatarMoeda(parseFloat(pagamento.valor))} for aprovado no Cartão de ${tipoCartao} na máquina ${pagamento.numero_serial}.`,
                                                showCancelButton: true,
                                                confirmButtonText: 'Confirmar Pagamento',
                                                cancelButtonText: 'Cancelar',
                                                reverseButtons: true
                                            }).then(async (result) => {
                                                if (result.isConfirmed) {
                                                    try {
                                                        const rota = pagamento.tipo_pagamento === 'pagamento_credito'
                                                            ? `/comanda-pagamentos/${pagamento.id}/processar-cartao-credito`
                                                            : `/comanda-pagamentos/${pagamento.id}/processar-cartao-debito`;

                                                        const pagamentoResponse = await api.post(rota);

                                                        if (pagamentoResponse.data.sucesso) {
                                                            pagamento.status_pagamento = 'aprovado';
                                                            handleChangePagamento(pagamento.id, 'status_pagamento', 'aprovado');
                                                            await Swal.fire('Sucesso', 'Pagamento com cartão registrado!', 'success');
                                                            resolve("aprovado");
                                                        } else {
                                                            pagamento.status_pagamento = 'erro';
                                                            handleChangePagamento(pagamento.id, 'status_pagamento', 'erro');
                                                            await Swal.fire('Erro', 'Não foi possível registrar o pagamento', 'error');
                                                            todosProcessadosComSucesso = false;
                                                            resolve("erro");
                                                        }
                                                    } catch (error) {
                                                        pagamento.status_pagamento = 'erro';
                                                        handleChangePagamento(pagamento.id, 'status_pagamento', 'erro');
                                                        await Swal.fire('Erro', 'Erro ao registrar pagamento com cartão', 'error');
                                                        todosProcessadosComSucesso = false;
                                                        resolve("erro");
                                                    }
                                                } else {
                                                    pagamento.status_pagamento = 'pendente';
                                                    handleChangePagamento(pagamento.id, 'status_pagamento', 'pendente');
                                                    todosProcessadosComSucesso = false;
                                                    resolve("cancelado");
                                                }
                                            });
                                        });
                                    };

                                    await confirmarPagamentoCartao();
                                } else {
                                    todosProcessadosComSucesso = false;
                                    await Swal.fire('Erro', 'Selecione uma máquina para o pagamento com cartão', 'error');
                                }
                            } else if (pagamento.tipo_pagamento === 'pagamento_pix') {
                                const confirmarPagamentoPix = () => {
                                    return new Promise((resolve) => {
                                        Swal.fire({
                                            title: 'Confirmar Pagamento PIX',
                                            text: `Por favor, confirme quando o PIX de ${formatarMoeda(parseFloat(pagamento.valor))} for recebido.`,
                                            showCancelButton: true,
                                            confirmButtonText: 'Confirmar Recebimento',
                                            cancelButtonText: 'Cancelar',
                                            reverseButtons: true
                                        }).then(async (result) => {
                                            if (result.isConfirmed) {
                                                try {
                                                    const pagamentoResponse = await api.post(
                                                        `/comanda-pagamentos/${pagamento.id}/processar-pix`
                                                    );

                                                    if (pagamentoResponse.data.sucesso) {
                                                        pagamento.status_pagamento = 'aprovado';
                                                        handleChangePagamento(pagamento.id, 'status_pagamento', 'aprovado');
                                                        await Swal.fire('Sucesso', 'Pagamento PIX registrado!', 'success');
                                                        resolve("aprovado");
                                                    } else {
                                                        pagamento.status_pagamento = 'erro';
                                                        handleChangePagamento(pagamento.id, 'status_pagamento', 'erro');
                                                        await Swal.fire('Erro', 'Não foi possível registrar o pagamento', 'error');
                                                        todosProcessadosComSucesso = false;
                                                        resolve("erro");
                                                    }
                                                } catch (error) {
                                                    pagamento.status_pagamento = 'erro';
                                                    handleChangePagamento(pagamento.id, 'status_pagamento', 'erro');
                                                    await Swal.fire('Erro', 'Erro ao registrar pagamento PIX', 'error');
                                                    todosProcessadosComSucesso = false;
                                                    resolve("erro");
                                                }
                                            } else {
                                                pagamento.status_pagamento = 'pendente';
                                                handleChangePagamento(pagamento.id, 'status_pagamento', 'pendente');
                                                todosProcessadosComSucesso = false;
                                                resolve("cancelado");
                                            }
                                        });
                                    });
                                };

                                await confirmarPagamentoPix();
                            } else if (pagamento.tipo_pagamento === 'pagamento_dinheiro') {
                                const confirmarPagamentoDinheiro = () => {
                                    return new Promise((resolve) => {
                                        const troco = calcularTroco();
                                        Swal.fire({
                                            title: 'Confirmar Pagamento',
                                            text: `Por favor, confirme quando receber o valor de ${formatarMoeda(parseFloat(pagamento.valor))} em dinheiro do cliente.${troco > 0 ? ` Será necessário dar um troco de ${formatarMoeda(troco)}.` : ''}`,
                                            showCancelButton: true,
                                            confirmButtonText: 'Confirmar Recebimento',
                                            cancelButtonText: 'Cancelar',
                                            reverseButtons: true
                                        }).then(async (result) => {
                                            if (result.isConfirmed) {
                                                try {
                                                    const pagamentoResponse = await api.post(
                                                        `/comanda-pagamentos/${pagamento.id}/processar-dinheiro`,
                                                        { troco }
                                                    );

                                                    if (pagamentoResponse.data.sucesso) {
                                                        pagamento.status_pagamento = 'aprovado';
                                                        handleChangePagamento(pagamento.id, 'status_pagamento', 'aprovado');
                                                        await Swal.fire('Sucesso', 'Pagamento em dinheiro registrado!', 'success');
                                                        resolve("aprovado");
                                                    } else {
                                                        pagamento.status_pagamento = 'erro';
                                                        handleChangePagamento(pagamento.id, 'status_pagamento', 'erro');
                                                        await Swal.fire('Erro', 'Não foi possível registrar o pagamento', 'error');
                                                        todosProcessadosComSucesso = false;
                                                        resolve("erro");
                                                    }
                                                } catch (error) {
                                                    pagamento.status_pagamento = 'erro';
                                                    handleChangePagamento(pagamento.id, 'status_pagamento', 'erro');
                                                    await Swal.fire('Erro', 'Erro ao registrar pagamento em dinheiro', 'error');
                                                    todosProcessadosComSucesso = false;
                                                    resolve("erro");
                                                }
                                            } else {
                                                pagamento.status_pagamento = 'pendente';
                                                handleChangePagamento(pagamento.id, 'status_pagamento', 'pendente');
                                                todosProcessadosComSucesso = false;
                                                resolve("cancelado");
                                            }
                                        });
                                    });
                                };

                                await confirmarPagamentoDinheiro();
                            } else if (pagamento.tipo_pagamento === 'uso_cortesia') {
                                const confirmarPagamentoUsoCortesia = () => {
                                    return new Promise((resolve) => {
                                        Swal.fire({
                                            title: 'Confirmar Pagamento',
                                            text: `Por favor, confirme o uso de cortesia de ${formatarMoeda(parseFloat(pagamento.valor))}`,
                                            showCancelButton: true,
                                            confirmButtonText: 'Confirmar Uso',
                                            cancelButtonText: 'Cancelar',
                                            reverseButtons: true
                                        }).then(async (result) => {
                                            if (result.isConfirmed) {
                                                try {
                                                    const pagamentoResponse = await api.post(`/comanda-pagamentos/${pagamento.id}/processar-uso-cortesia`);

                                                    if (pagamentoResponse.data.sucesso) {
                                                        pagamento.status_pagamento = 'aprovado';
                                                        handleChangePagamento(pagamento.id, 'status_pagamento', 'aprovado');
                                                        await Swal.fire('Sucesso', 'Cortesia registrada!', 'success');
                                                        resolve("aprovado");
                                                    } else {
                                                        pagamento.status_pagamento = 'erro';
                                                        handleChangePagamento(pagamento.id, 'status_pagamento', 'erro');
                                                        await Swal.fire('Erro', 'Não foi possível registrar a cortesia', 'error');
                                                        todosProcessadosComSucesso = false;
                                                        resolve("erro");
                                                    }
                                                } catch (error) {
                                                    pagamento.status_pagamento = 'erro';
                                                    handleChangePagamento(pagamento.id, 'status_pagamento', 'erro');
                                                    await Swal.fire('Erro', 'Erro ao registrar cortesia', 'error');
                                                    todosProcessadosComSucesso = false;
                                                    resolve("erro");
                                                }
                                            } else {
                                                pagamento.status_pagamento = 'pendente';
                                                handleChangePagamento(pagamento.id, 'status_pagamento', 'pendente');
                                                todosProcessadosComSucesso = false;
                                                resolve("cancelado");
                                            }
                                        });
                                    });
                                };

                                await confirmarPagamentoUsoCortesia();
                            } else if (pagamento.tipo_pagamento === 'pagamento_boleto') {
                                const confirmarPagamentoBoleto = () => {
                                    return new Promise((resolve) => {
                                        Swal.fire({
                                            title: 'Confirmar Pagamento',
                                            text: `Por favor, confirme quando o boleto de ${formatarMoeda(parseFloat(pagamento.valor))} for recebido.`,
                                            showCancelButton: true,
                                            confirmButtonText: 'Confirmar Recebimento',
                                            cancelButtonText: 'Cancelar',
                                            reverseButtons: true
                                        }).then(async (result) => {
                                            if (result.isConfirmed) {
                                                try {
                                                    const pagamentoResponse = await api.post(`/comanda-pagamentos/${pagamento.id}/processar-boleto`);

                                                    if (pagamentoResponse.data.sucesso) {
                                                        pagamento.status_pagamento = 'aprovado';
                                                        handleChangePagamento(pagamento.id, 'status_pagamento', 'aprovado');
                                                        await Swal.fire('Sucesso', 'Pagamento via boleto registrado!', 'success');
                                                        resolve("aprovado");
                                                    } else {
                                                        pagamento.status_pagamento = 'erro';
                                                        handleChangePagamento(pagamento.id, 'status_pagamento', 'erro');
                                                        await Swal.fire('Erro', 'Não foi possível registrar o pagamento', 'error');
                                                        todosProcessadosComSucesso = false;
                                                        resolve("erro");
                                                    }
                                                } catch (error) {
                                                    pagamento.status_pagamento = 'erro';
                                                    handleChangePagamento(pagamento.id, 'status_pagamento', 'erro');
                                                    await Swal.fire('Erro', 'Erro ao registrar pagamento via boleto', 'error');
                                                    todosProcessadosComSucesso = false;
                                                    resolve("erro");
                                                }
                                            } else {
                                                pagamento.status_pagamento = 'pendente';
                                                handleChangePagamento(pagamento.id, 'status_pagamento', 'pendente');
                                                todosProcessadosComSucesso = false;
                                                resolve("cancelado");
                                            }
                                        });
                                    });
                                };

                                await confirmarPagamentoBoleto();
                            } else if (pagamento.tipo_pagamento === 'pagamento_ted') {
                                const confirmarPagamentoTed = () => {
                                    return new Promise((resolve) => {
                                        Swal.fire({
                                            title: 'Confirmar Pagamento',
                                            text: `Por favor, confirme quando a TED de ${formatarMoeda(parseFloat(pagamento.valor))} for recebida.`,
                                            showCancelButton: true,
                                            confirmButtonText: 'Confirmar Recebimento',
                                            cancelButtonText: 'Cancelar',
                                            reverseButtons: true
                                        }).then(async (result) => {
                                            if (result.isConfirmed) {
                                                try {
                                                    const pagamentoResponse = await api.post(`/comanda-pagamentos/${pagamento.id}/processar-ted`);

                                                    if (pagamentoResponse.data.sucesso) {
                                                        pagamento.status_pagamento = 'aprovado';
                                                        handleChangePagamento(pagamento.id, 'status_pagamento', 'aprovado');
                                                        await Swal.fire('Sucesso', 'Pagamento via TED registrado!', 'success');
                                                        resolve("aprovado");
                                                    } else {
                                                        pagamento.status_pagamento = 'erro';
                                                        handleChangePagamento(pagamento.id, 'status_pagamento', 'erro');
                                                        await Swal.fire('Erro', 'Não foi possível registrar o pagamento', 'error');
                                                        todosProcessadosComSucesso = false;
                                                        resolve("erro");
                                                    }
                                                } catch (error) {
                                                    pagamento.status_pagamento = 'erro';
                                                    handleChangePagamento(pagamento.id, 'status_pagamento', 'erro');
                                                    await Swal.fire('Erro', 'Erro ao registrar pagamento via TED', 'error');
                                                    todosProcessadosComSucesso = false;
                                                    resolve("erro");
                                                }
                                            } else {
                                                pagamento.status_pagamento = 'pendente';
                                                handleChangePagamento(pagamento.id, 'status_pagamento', 'pendente');
                                                todosProcessadosComSucesso = false;
                                                resolve("cancelado");
                                            }
                                        });
                                    });
                                };

                                await confirmarPagamentoTed();
                            } else if (pagamento.tipo_pagamento === 'pagamento_doc') {
                                const confirmarPagamentoDoc = () => {
                                    return new Promise((resolve) => {
                                        Swal.fire({
                                            title: 'Confirmar Pagamento',
                                            text: `Por favor, confirme quando o DOC de ${formatarMoeda(parseFloat(pagamento.valor))} for recebido.`,
                                            showCancelButton: true,
                                            confirmButtonText: 'Confirmar Recebimento',
                                            cancelButtonText: 'Cancelar',
                                            reverseButtons: true
                                        }).then(async (result) => {
                                            if (result.isConfirmed) {
                                                try {
                                                    const pagamentoResponse = await api.post(`/comanda-pagamentos/${pagamento.id}/processar-doc`);

                                                    if (pagamentoResponse.data.sucesso) {
                                                        pagamento.status_pagamento = 'aprovado';
                                                        handleChangePagamento(pagamento.id, 'status_pagamento', 'aprovado');
                                                        await Swal.fire('Sucesso', 'Pagamento via DOC registrado!', 'success');
                                                        resolve("aprovado");
                                                    } else {
                                                        pagamento.status_pagamento = 'erro';
                                                        handleChangePagamento(pagamento.id, 'status_pagamento', 'erro');
                                                        await Swal.fire('Erro', 'Não foi possível registrar o pagamento', 'error');
                                                        todosProcessadosComSucesso = false;
                                                        resolve("erro");
                                                    }
                                                } catch (error) {
                                                    pagamento.status_pagamento = 'erro';
                                                    handleChangePagamento(pagamento.id, 'status_pagamento', 'erro');
                                                    await Swal.fire('Erro', 'Erro ao registrar pagamento via DOC', 'error');
                                                    todosProcessadosComSucesso = false;
                                                    resolve("erro");
                                                }
                                            } else {
                                                pagamento.status_pagamento = 'pendente';
                                                handleChangePagamento(pagamento.id, 'status_pagamento', 'pendente');
                                                todosProcessadosComSucesso = false;
                                                resolve("cancelado");
                                            }
                                        });
                                    });
                                };

                                await confirmarPagamentoDoc();
                            }
                        } catch (pagamentoError) {
                            todosProcessadosComSucesso = false;
                        }
                    }

                    for (let i = 0; i < formData.pagamentos.length; i++) {
                        const pagamento = formData.pagamentos[i];
                        if (pagamento.status_pagamento !== 'aprovado') {
                            todosProcessadosComSucesso = false;
                            break;
                        }
                    }

                    return todosProcessadosComSucesso;
                };

                const sucessoNoProcessamento = await processarPagamentos();

                if (sucessoNoProcessamento) {
                    try {
                        await api.post(`/comandas/${formData.id}/encerrar`);
                        Swal.fire('Sucesso', 'Comanda finalizada com sucesso!', 'success');
                        onComandaAtualizada();
                        onClose();
                    } catch (error) {
                        Swal.fire('Erro', `${error.response.data.message}`, 'error');
                    }
                }
            } else {
                Swal.close();
                Swal.fire('Erro', 'É necessário salvar a comanda antes de finalizá-la', 'error');
            }
        }
        catch (error) {
            Swal.close();
            Swal.fire('Erro', `${error.response.data.message}`, 'error');
        }
    };

    const cancelarComanda = async () => {
        if (!formData.observacao || !formData.observacao.trim()) {
            Swal.fire('Atenção', 'Por favor, preencha o campo de observações com o motivo do cancelamento antes de cancelar a comanda.', 'warning');
            return;
        }

        const result = await Swal.fire({
            title: 'Cancelar Comanda',
            text: `Deseja cancelar a comanda #${formData.id}?`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Sim, cancelar',
            cancelButtonText: 'Não'
        });

        if (result.isConfirmed) {
            try {
                Swal.fire({
                    title: "Carregando...",
                    allowOutsideClick: false,
                    allowEscapeKey: false,
                    showConfirmButton: false,
                    didOpen: () => {
                        Swal.showLoading();
                    }
                });

                const response = await api.put(`/comandas/${formData.id}/cancelar-aberta`, {
                    observacao: formData.observacao
                });

                if (response.data.success) {
                    await Swal.fire('Sucesso!', 'Comanda cancelada com sucesso.', 'success');
                    onComandaAtualizada();
                    onClose();
                } else {
                    Swal.fire('Erro', response.data.message || 'Erro ao cancelar a comanda.', 'error');
                }
            } catch (error) {
                Swal.close();
                Swal.fire('Erro', error.response?.data?.message || 'Erro ao cancelar a comanda.', 'error');
            }
        }
    };

    return (
        <div className="modal fade show" tabIndex="-1" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-xl modal-dialog-scrollable modal-dialog-centered" style={{
                maxWidth: windowWidth < 1128 ? '90%' : '70%',
                margin: '0.5rem auto'
            }}>
                <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                    <div className="modal-header">
                        <h4 className="fw-bold mt-2">{tituloModal}</h4>
                        <button type="button" className="btn-close" onClick={onClose} aria-label="Close"></button>
                    </div>

                    <div className="modal-body">
                        <form onSubmit={handleSubmit}>
                            <ul className="nav nav-tabs mt-3">
                                <li className="nav-item">
                                    <a
                                        className={activeTab === 'itens' ? 'nav-link active' : 'nav-link'}
                                        aria-current="page"
                                        onClick={() => setActiveTab('itens')}>
                                        Itens</a>
                                </li>
                                <li className="nav-item">
                                    <a
                                        className={activeTab === 'pagamento' ? 'nav-link active' : 'nav-link'}
                                        aria-current="page"
                                        onClick={() => setActiveTab('pagamento')}>
                                        Pagamento</a>
                                </li>
                                <li className="nav-item">
                                    <a
                                        className={activeTab === 'informacoes' ? 'nav-link active' : 'nav-link'}
                                        aria-current="page"
                                        onClick={() => setActiveTab('informacoes')}>
                                        Informações</a>
                                </li>
                            </ul>

                            {activeTab === 'itens' && (
                                <div>
                                    <div className="row mt-3">
                                        <div className="col-lg-6 col-12">
                                            <label className="form-label fw-bold">Cliente</label>
                                            <Select
                                                options={clientesOptions}
                                                styles={customStyles}
                                                onInputChange={(value) => {
                                                    handleAutoCompleteClientes(value);
                                                }}
                                                onFocus={handleFocusClientes}
                                                onChange={(value) => handleSelectChange('cliente', value)}
                                                placeholder="Digite o nome do cliente"
                                                isClearable
                                                menuPlacement="auto"
                                                menuPosition="fixed"
                                                isDisabled={comandaAtual != null}
                                                noOptionsMessage={() =>
                                                    inputPesquisaCliente.length < 3 ? null : "Sem opções"
                                                }
                                                value={formData.cliente}
                                            />
                                        </div>
                                        <div className="col-lg-4 col-12 mt-lg-0 mt-3">
                                            <label className="form-label fw-bold">Data da Comanda</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                value={formData.data_abertura instanceof Date ? moment(formData.data_abertura).format('DD/MM/YYYY HH:mm:ss') : formData.data_abertura}
                                                disabled
                                                readOnly
                                            />
                                        </div>
                                        <div className="col-lg-2 col-12 mt-lg-0 mt-3">
                                            <label className="form-label fw-bold">N°</label>
                                            <input
                                                type="text"
                                                name="id"
                                                className="form-control"
                                                autoComplete="off"
                                                disabled
                                                value={formData.id}
                                            />
                                        </div>
                                    </div>
                                    <div className="row mt-5">
                                        <div className="col-12">
                                            <div className="table-responsive max-h-400" style={{ maxHeight: '45vh' }}>
                                                <table className="table">
                                                    <thead className="table-custom-primary">
                                                        <tr>
                                                            <th>Item</th>
                                                            <th>Profissional</th>
                                                            <th>Qtd.</th>
                                                            <th className="text-nowrap">Valor (R$)</th>
                                                            <th className="text-nowrap">Desc. (%)</th>
                                                            <th className="text-nowrap">Final (R$)</th>
                                                            <th className="text-center">Ações</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {formData.itens.map((item) => (
                                                            <tr key={item.id}>
                                                                <td className="text-nowrap">
                                                                    <div style={{ width: '350px' }}>
                                                                        {item.tipo_item === "servico" ? (
                                                                            <Select
                                                                                options={servicosOptions}
                                                                                styles={customStyles}
                                                                                onInputChange={(value) => {
                                                                                    handleAutoCompleteServicos(value);
                                                                                }}
                                                                                onFocus={handleFocusServicos}
                                                                                onChange={(value) => handleChangeItem(item.id, "servico", value)}
                                                                                placeholder="Digite o nome do serviço"
                                                                                isClearable
                                                                                menuPlacement="auto"
                                                                                menuPosition="fixed"
                                                                                isDisabled={!item.editar}
                                                                                noOptionsMessage={() => "Sem opções"}
                                                                                value={item.servico}
                                                                            />
                                                                        ) : item.tipo_item === "produto" ? (
                                                                            <Select
                                                                                options={produtosOptions}
                                                                                styles={customStyles}
                                                                                onInputChange={(value) => {
                                                                                    handleAutoCompleteProdutos(value);
                                                                                }}
                                                                                onFocus={handleFocusProdutos}
                                                                                onChange={(value) => handleChangeItem(item.id, "produto", value)}
                                                                                placeholder="Digite o nome do produto"
                                                                                isClearable
                                                                                menuPlacement="auto"
                                                                                menuPosition="fixed"
                                                                                isDisabled={!item.editar}
                                                                                noOptionsMessage={() => "Sem opções"}
                                                                                value={item.produto}
                                                                            />
                                                                        ) : (
                                                                            <span className="form-control-plaintext fw-bold">Caixinha</span>
                                                                        )}
                                                                    </div>
                                                                </td>
                                                                <td className="text-nowrap">
                                                                    <div style={{ width: '350px' }}>
                                                                        <Select
                                                                            options={colaboradoresOptions}
                                                                            styles={customStyles}
                                                                            onInputChange={(value) => {
                                                                                handleAutoCompleteColaboradores(value, item);
                                                                            }}
                                                                            onFocus={() => handleFocusColaboradoresComanda(item)}
                                                                            onChange={(value) => handleChangeItem(item.id, "profissional", value)}
                                                                            placeholder="Digite o nome do colaborador"
                                                                            isClearable
                                                                            menuPlacement="auto"
                                                                            menuPosition="fixed"
                                                                            isDisabled={!item.editar || (item.tipo_item !== 'caixinha' && item.servico == '' && item.produto == '')}
                                                                            noOptionsMessage={() => "Sem opções"}
                                                                            value={item.profissional}
                                                                        />
                                                                        
                                                                        {item.tipo_item === 'servico' && item.servico && (
                                                                            <>
                                                                                {!item.editar && item.assistentes && item.assistentes.length > 0 && (
                                                                                    <div className="mt-2">
                                                                                        <button
                                                                                            type="button"
                                                                                            className="btn btn-link btn-sm p-0 text-decoration-none"
                                                                                            disabled
                                                                                        >
                                                                                            <FaUserPlus className="me-1" />
                                                                                            Assistentes:
                                                                                        </button>
                                                                                    </div>
                                                                                )}
                                                                                {item.assistentes && item.assistentes.length > 0 && (
                                                                                    <div className="mt-2">
                                                                                        {(item.assistentes || []).map((assistente, index) => (
                                                                                            item.editar ? (
                                                                                                <div key={assistente.id || index} className="mb-2">
                                                                                                    <label className="form-label fw-bold">Assistente</label>
                                                                                                    <div className="d-flex align-items-center">
                                                                                                        <Select
                                                                                                            options={colaboradoresOptions.filter(opt => 
                                                                                                                opt.value !== item.profissional?.value && 
                                                                                                                !(item.assistentes || []).some(a => a.id !== assistente.id && a.profissional?.value === opt.value)
                                                                                                            )}
                                                                                                            styles={customStyles}
                                                                                                            onInputChange={(value) => {
                                                                                                                handleAutoCompleteColaboradores(value, item);
                                                                                                            }}
                                                                                                            onChange={(value) => handleChangeAssistente(item.id, assistente.id, "profissional", value)}
                                                                                                            placeholder="Selecione assistente"
                                                                                                            isClearable
                                                                                                            menuPlacement="auto"
                                                                                                            menuPosition="fixed"
                                                                                                            noOptionsMessage={() =>
                                                                                                                inputPesquisaColaborador.length < 3 ? null : "Sem opções"
                                                                                                            }
                                                                                                            value={assistente.profissional}
                                                                                                            className="flex-grow-1"
                                                                                                        />
                                                                                                        <button
                                                                                                            className="btn btn-outline-danger ms-2"
                                                                                                            type="button"
                                                                                                            onClick={() => handleRemoverAssistente(item.id, assistente.id)}
                                                                                                        >
                                                                                                            <FaUserMinus />
                                                                                                        </button>
                                                                                                    </div>
                                                                                                </div>
                                                                                            ) : (
                                                                                                <div key={index} className="mb-2">
                                                                                                    <Select
                                                                                                        options={[]}
                                                                                                        styles={customStyles}
                                                                                                        placeholder=""
                                                                                                        isDisabled={true}
                                                                                                        value={assistente.profissional}
                                                                                                    />
                                                                                                </div>
                                                                                            )
                                                                                        ))}
                                                                                    </div>
                                                                                )}
                                                                                {item.editar && item.servico && item.profissional && (
                                                                                    <div className="mt-2">
                                                                                        <button
                                                                                            type="button"
                                                                                            className="btn btn-link btn-sm p-0 text-decoration-none"
                                                                                            onClick={() => handleAdicionarAssistente(item.id)}
                                                                                        >
                                                                                            <FaUserPlus className="me-1" />
                                                                                            Adicionar Assistente
                                                                                        </button>
                                                                                    </div>
                                                                                )}
                                                                            </>
                                                                        )}
                                                                    </div>
                                                                </td>
                                                                <td>
                                                                    <input
                                                                        type="number"
                                                                        name="qtd"
                                                                        className="form-control"
                                                                        autoComplete="off"
                                                                        value={item.qtd}
                                                                        style={{ width: '80px' }}
                                                                        onChange={(e) => handleChangeItem(item.id, "qtd", e.target.value)}
                                                                        disabled={item.tipo_item === 'caixinha' || (item.servico == '' && item.produto == '') || !item.editar}
                                                                        required
                                                                    />
                                                                </td>
                                                                <td className="text-nowrap">
                                                                    <CurrencyInput
                                                                        name="valor_item"
                                                                        placeholder="R$ 0,00"
                                                                        value={item.valor_item}
                                                                        decimalsLimit={2}
                                                                        decimalSeparator=","
                                                                        groupSeparator="."
                                                                        prefix="R$ "
                                                                        className="form-control"
                                                                        disabled={!item.editar || (item.tipo_item !== 'caixinha' && item.servico == '' && item.produto == '')}
                                                                        autoComplete="off"
                                                                        style={{ width: '150px' }}
                                                                        onValueChange={(value) =>
                                                                            handleChangeItem(item.id, "valor_item", value)
                                                                        }
                                                                    />
                                                                </td>
                                                                <td className="text-nowrap">
                                                                    <input
                                                                        className="form-control"
                                                                        name="desconto_item"
                                                                        autoComplete="off"
                                                                        style={{ width: '80px' }}
                                                                        disabled={item.tipo_item === 'caixinha' || (item.servico == '' && item.produto == '') || !item.editar}
                                                                        value={item.desconto_item}
                                                                        onChange={(event) => handleChangeItem(item.id, "desconto_item", event.target.value)}
                                                                        placeholder="0%"
                                                                    />
                                                                </td>
                                                                <td className="text-nowrap">
                                                                    {item.final_item != ''
                                                                        ? parseFloat(item.final_item).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
                                                                        : 'R$ 0,00'}
                                                                </td>
                                                                <td className="text-center text-nowrap">
                                                                    <button
                                                                        className="btn btn-custom-secondary btn-sm"
                                                                        type="button"
                                                                        onClick={() => handleEditItem(item)}
                                                                    >
                                                                        <FaEdit />
                                                                    </button>
                                                                    <button
                                                                        className="btn btn-custom-secondary btn-sm ms-3"
                                                                        type="button"
                                                                        onClick={() => handleDeleteItem(item)}
                                                                    >
                                                                        <FaTrashAlt />
                                                                    </button>
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="row mt-3">
                                        <div className="col-12 text-end">
                                            <div className="d-lg-block d-grid gap-2">
                                                <button
                                                    className="btn btn-outline-primary"
                                                    type="button"
                                                    onClick={() => handleAdicionarItem('servico')}
                                                >
                                                    <FaPlus /> Adicionar Serviço
                                                </button>
                                                <button
                                                    className="btn btn-outline-primary ms-lg-3"
                                                    type="button"
                                                    onClick={() => handleAdicionarItem('produto')}
                                                >
                                                    <FaPlus /> Adicionar Produto
                                                </button>
                                                <button
                                                    className="btn btn-outline-primary ms-lg-3"
                                                    type="button"
                                                    onClick={() => handleAdicionarItem('caixinha')}
                                                >
                                                    <FaThumbsUp /> Caixinha
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {activeTab === 'pagamento' && (
                                <div>
                                    <div className="row mt-3 align-items-end">
                                        <div className="col-lg-4 col-12">
                                            <label className="form-label fw-bold">Caixa Responsável</label>
                                            <select className="form-select"
                                                value={formData.caixa_responsavel}
                                                onChange={(e) => handleSelectChange('caixa_responsavel', e.target.value)}>
                                                <option value="" hidden>Selecione...</option>
                                                {caixas.map((caixa) => (
                                                    <option key={caixa.id} value={caixa.id}>{caixa.colaborador.nome} - {moment(caixa.data_abertura).format('DD/MM/YYYY')}</option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className="col-lg-2 col-12 mt-lg-0 mt-3">
                                            <label className="form-label fw-bold">Saldo do Cliente</label>
                                            <h4 className="form-label fw-bold">R$ 0,00</h4>
                                        </div>
                                        <div className="col-lg-2 col-12 mt-lg-0 mt-3">
                                            <label className="form-label fw-bold">Total da Comanda</label>
                                            <h4 className="form-label fw-bold">{calcularTotal(formData.itens)}</h4>
                                        </div>
                                        <div className="col-lg-2 col-12 mt-lg-0 mt-3">
                                            <label className="form-label fw-bold">Total a Cobrar</label>
                                            <h4 className="form-label fw-bold text-success">{calcularTotal(formData.itens)}</h4>
                                        </div>
                                        <div className="col-lg-2 col-12 mt-lg-0 mt-3">
                                            <label className="form-label fw-bold">Troco</label>
                                            <h4 className="form-label fw-bold" style={{
                                                color: calcularTroco() >= 0 ? '#198754' : '#dc3545'
                                            }}>
                                                {formatarMoeda(calcularTroco())}
                                            </h4>
                                        </div>
                                    </div>
                                    <div className="row mt-5">
                                        <div className="col-12">
                                            <div className="table-responsive max-h-400" style={{ maxHeight: '45vh' }}>
                                                <table className="table">
                                                    <thead className="table-custom-primary">
                                                        <tr>
                                                            <th className="text-nowrap">Forma de Pagamento</th>
                                                            <th>Informações</th>
                                                            <th className="text-nowrap">Conta Bancária</th>
                                                            <th className="text-nowrap">Bandeira</th>
                                                            <th className="text-nowrap">Máquina</th>
                                                            <th className="text-nowrap">Valor (R$)</th>
                                                            <th className="text-nowrap">Status</th>
                                                            <th className="text-center">Ações</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {formData.pagamentos.map((item) => (
                                                            <tr key={item.id}>
                                                                <td className="text-nowrap">
                                                                    <select className="form-select"
                                                                        value={item.tipo_pagamento}
                                                                        style={{ width: '200px' }}
                                                                        disabled={item.status_pagamento === 'aprovado'}
                                                                        onChange={(e) => handleChangePagamento(item.id, 'tipo_pagamento', e.target.value)}>
                                                                        <option value="pagamento_dinheiro">Dinheiro</option>
                                                                        <option value="pagamento_credito">Cartão Crédito</option>
                                                                        <option value="pagamento_debito">Cartão Débito</option>
                                                                        <option value="pagamento_pix">Pix</option>
                                                                        <option value="pagamento_boleto">Boleto</option>
                                                                        <option value="pagamento_ted">TED</option>
                                                                        <option value="pagamento_doc">DOC</option>
                                                                        <option value="uso_cortesia">Cortesia</option>
                                                                    </select>
                                                                </td>
                                                                <td className="text-nowrap">
                                                                    <input
                                                                        type="text"
                                                                        name="informacoes"
                                                                        className="form-control"
                                                                        autoComplete="off"
                                                                        value={item.informacoes}
                                                                        disabled={item.status_pagamento === 'aprovado'}
                                                                        onChange={(e) =>
                                                                            handleChangePagamento(item.id, "informacoes", e.target.value)
                                                                        }
                                                                        required
                                                                        style={{ width: '200px' }}
                                                                    />
                                                                </td>
                                                                <td className="text-nowrap">
                                                                    {(item.tipo_pagamento === 'pagamento_pix' || item.tipo_pagamento === 'pagamento_credito' || item.tipo_pagamento === 'pagamento_debito') ? (
                                                                        <select
                                                                            className="form-select"
                                                                            style={{ width: '200px' }}
                                                                            value={item.conta_bancaria_id || ''}
                                                                            disabled={item.status_pagamento === 'aprovado'}
                                                                            onChange={(e) => handleChangePagamento(item.id, 'conta_bancaria_id', e.target.value)}
                                                                            required={item.tipo_pagamento === 'pagamento_pix'}
                                                                        >
                                                                            <option value="">Selecione...</option>
                                                                            {contasBancarias.map(conta => (
                                                                                <option key={conta.id} value={conta.id}>
                                                                                    {conta.nome_exibicao || conta.nome}
                                                                                </option>
                                                                            ))}
                                                                        </select>
                                                                    ) : (
                                                                        <div style={{ width: '200px' }}>-</div>
                                                                    )}
                                                                </td>
                                                                <td className="text-nowrap">
                                                                    {(item.tipo_pagamento === 'pagamento_credito' || item.tipo_pagamento === 'pagamento_debito') && item.conta_bancaria_id ? (
                                                                        <select
                                                                            className="form-select"
                                                                            style={{ width: '200px' }}
                                                                            value={item.bandeira_id || ''}
                                                                            disabled={item.status_pagamento === 'aprovado'}
                                                                            onChange={(e) => handleChangePagamento(item.id, 'bandeira_id', e.target.value)}
                                                                        >
                                                                            <option value="">Selecione...</option>
                                                                            {(bandeirasCartao[item.id] || []).map(bandeira => (
                                                                                <option key={bandeira.id} value={bandeira.id}>
                                                                                    {bandeira.bandeira}{bandeira.tipo_exibicao ? ` - ${bandeira.tipo_exibicao}` : ''}
                                                                                </option>
                                                                            ))}
                                                                        </select>
                                                                    ) : (
                                                                        <div style={{ width: '200px' }}>-</div>
                                                                    )}
                                                                </td>
                                                                <td className="text-nowrap">
                                                                    {(item.tipo_pagamento === 'pagamento_credito' || item.tipo_pagamento === 'pagamento_debito') ? (
                                                                        <select
                                                                            className="form-select"
                                                                            style={{ width: '250px' }}
                                                                            value={item.numero_serial}
                                                                            disabled={item.status_pagamento === 'aprovado'}
                                                                            onChange={(e) => handleChangePagamento(item.id, 'numero_serial', e.target.value)}
                                                                        >
                                                                            <option value="" hidden>Selecione...</option>
                                                                            {maquinasList.map(maquina => (
                                                                                <option key={maquina.id} value={maquina.serial}>
                                                                                    {maquina.apelido} - {maquina.serial}
                                                                                </option>
                                                                            ))}
                                                                        </select>
                                                                    ) : (
                                                                        <div style={{ width: '250px' }}>-</div>
                                                                    )}
                                                                </td>
                                                                <td className="text-nowrap">
                                                                    <CurrencyInput
                                                                        name="valor"
                                                                        placeholder="R$ 0,00"
                                                                        value={item.valor}
                                                                        decimalsLimit={2}
                                                                        decimalSeparator=","
                                                                        groupSeparator="."
                                                                        prefix="R$ "
                                                                        className="form-control"
                                                                        autoComplete="off"
                                                                        disabled={item.status_pagamento === 'aprovado'}
                                                                        style={{ width: '150px' }}
                                                                        onValueChange={(value) =>
                                                                            handleChangePagamento(item.id, "valor", value)
                                                                        }
                                                                    />
                                                                </td>
                                                                <td className="text-nowrap">
                                                                    <span className={`badge ${item.status_pagamento === 'aprovado' ? 'bg-success' :
                                                                        item.status_pagamento === 'erro' ? 'bg-danger' :
                                                                            'bg-warning'
                                                                        }`} style={{ fontSize: '0.9rem', padding: '8px' }}>
                                                                        {item.status_pagamento === 'aprovado' ? 'Aprovado' :
                                                                            item.status_pagamento === 'erro' ? 'Erro' :
                                                                                'Pendente'}
                                                                    </span>
                                                                </td>
                                                                <td className="text-center text-nowrap">
                                                                    {item.status_pagamento !== 'aprovado' && (
                                                                        <button
                                                                            className="btn btn-custom-secondary btn-sm"
                                                                            type="button"
                                                                            onClick={() => handleDeletePagamento(item)}
                                                                        >
                                                                            <FaTrashAlt />
                                                                        </button>
                                                                    )}
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="row mt-3">
                                        <div className="col-12 text-end">
                                            <button
                                                className="btn btn-outline-primary"
                                                type="button"
                                                onClick={handleAdicionarPagamento}
                                            >
                                                <FaPlus /> Adicionar Forma de Pagamento
                                            </button>
                                        </div>
                                    </div>
                                    <div className="row mt-3">
                                        <div className="col-12 d-flex justify-content-end">
                                            {/* <div className="form-check mb-3">
                                                <input
                                                    type="checkbox"
                                                    className="form-check-input"
                                                />
                                                <label className="form-check-label">Salvar troco como dívida</label>
                                            </div> */}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {activeTab === 'informacoes' && (
                                <div>
                                    <div className="row mt-3">
                                        <div className="col-md-12 mt-lg-0 mt-3">
                                            <label className="form-label fw-bold">Observações</label>
                                            <textarea
                                                name="observacao"
                                                className="form-control"
                                                autoComplete="off"
                                                rows="4"
                                                value={formData.observacao}
                                                onChange={handleChange}
                                            ></textarea>
                                        </div>
                                    </div>
                                    <div className="row mt-3">
                                        <div className="col-lg-6 col-12">
                                            {comandaAtual != null && comandaAtual?.criado_por != null && (
                                                <>
                                                    <label className="form-label fw-bold">Criado por: </label>
                                                    <span className="form-label"> {comandaAtual.criado_por}</span>
                                                    <br />
                                                    <label className="form-label fw-bold">Criado em: </label>
                                                    <span className="form-label"> {moment(comandaAtual.criado_em).format('DD/MM/YYYY HH:mm:ss')}</span>
                                                </>
                                            )}
                                        </div>
                                        <div className="col-lg-6 col-12">
                                            {comandaAtual != null && comandaAtual?.alterado_por != null && (
                                                <>
                                                    <label className="form-label fw-bold">Atualizado por: </label>
                                                    <span className="form-label"> {comandaAtual.alterado_por}</span>
                                                    <br />
                                                    <label className="form-label fw-bold">Atualizado em: </label>
                                                    <span className="form-label"> {moment(comandaAtual.alterado_em).format('DD/MM/YYYY HH:mm:ss')}</span>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </form>
                    </div>
                    <div className="modal-footer mt-4">
                        <div className="row">
                            <div className="col-12 text-end">
                                <div className="d-lg-block d-grid gap-2">
                                    {formData.status == 'aberta' && comandaSalva && (
                                        <button className="btn btn-outline-secondary" type="button" onClick={cancelarComanda} >
                                            <FaTimes /> Cancelar Comanda
                                        </button>
                                    )}
                                    <button className="btn btn-outline-danger ms-lg-3" type="button" onClick={onClose}>
                                        <FaTimes /> Fechar
                                    </button>
                                    {formData.status == 'aberta' && (
                                        <>
                                            <button className="btn btn-custom-primary ms-lg-3" type="submit" onClick={handleSubmit}>
                                                <FaRegSave /> Salvar
                                            </button>
                                            {comandaSalva && (
                                                <button className="btn btn-custom-primary ms-lg-3" type="button" onClick={handleFinalizarComanda} >
                                                    Finalizar Comanda
                                                </button>
                                            )}
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div >
        </div >
    );
};

export default ComandaAbertaModal;
